// Reading plans on the prototype data: the same shapes and rules as the API (08-READING-PLANS.md),
// in memory. The classic plans are the API's own catalogue: M'Cheyne and the 1662 lectionary (copied
// from biblestandard-api src/data/plans/ into ./catalog, loaded only when the prototype needs them)
// and the four built-in plans read from Day 1 (its migration 0009, same ids, made with buildSchedule).
// Friends' plans are made here with buildSchedule, two of them with a book read alongside (F7): the
// Shorter Catechism (its text is on the web) and Owen's Mortification (in the app only).
import { check } from "./mock";
import {
  addDays,
  appointedOrdinal,
  buildSchedule,
  DEFAULT_PROMPTS,
  localDate,
  normaliseSchedule,
  placeOn,
  placeToday,
  slotOn,
  type CompanionWork,
  type DayNote,
  type Invitation,
  type Journey,
  type Plan,
  type PlanSource,
  type PlanSummary,
} from "./plans";
import { ApiError } from "./problem";
import type { UserCard } from "./types";
import { loadCatalogue, loadToc, sectionReadings, type SectionReading } from "./works";

const wait = <T>(v: T, ms = 90) => new Promise<T>((r) => setTimeout(() => r(structuredClone(v)), ms));
const u = (id: string, handle: string, displayName: string): UserCard => ({ id, handle, displayName, avatarUrl: null });
const range = (a: number, b: number) => Array.from({ length: Math.max(0, b - a + 1) }, (_, i) => a + i);
const CREATED = "2026-10-09T00:00:00.000Z";

type CatalogFile = { id: string; packageId: string; title: string; description: string; scheduleKind: "calendar"; days: Plan["schedule"] };

const fromCatalog = (c: CatalogFile): Plan => ({
  id: c.id,
  classic: true,
  title: c.title,
  description: c.description,
  author: null,
  days: c.days.length,
  books: null,
  companion: null,
  companionWork: null,
  restSundays: false,
  visibility: "public",
  scheduleKind: c.scheduleKind,
  packageId: c.packageId,
  prompts: DEFAULT_PROMPTS,
  createdAt: CREATED,
  schedule: normaliseSchedule(c.days),
});

/** The built-in plans read from Day 1 (RP1), as the API's migration 0009 has them. */
const SEQUENCE_CLASSICS: { id: string; title: string; description: string; books: string[]; days: number; companionWork?: CompanionWork }[] = [
  { id: "0199c8a0-0000-7000-8000-000000000101", title: "John in 21 Days", description: "The fourth Gospel, a chapter a day: who Jesus is, in his own words and signs.", books: ["John"], days: 21 },
  { id: "0199c8a0-0000-7000-8000-000000000102", title: "Psalms in a Month", description: "All 150 psalms in thirty days, five a day: the prayer book of the church.", books: ["Ps"], days: 30 },
  { id: "0199c8a0-0000-7000-8000-000000000103", title: "Romans Slowly", description: "Paul's letter to Rome, one chapter a day with room to stop and think.", books: ["Rom"], days: 16 },
  {
    id: "0199c8a0-0000-7000-8000-000000000104",
    title: "Hebrews with Owen",
    description: "Hebrews a chapter a day, with John Owen's Exposition of the Epistle to the Hebrews beside it in the app.",
    books: ["Heb"],
    days: 13,
    companionWork: { libraryId: "john-owen-works-vol-h1", title: "An Exposition of the Epistle to the Hebrews", author: "John Owen" },
  },
];
export const CLASSIC_IDS = { john: SEQUENCE_CLASSICS[0]!.id, psalms: SEQUENCE_CLASSICS[1]!.id, romans: SEQUENCE_CLASSICS[2]!.id, hebrews: SEQUENCE_CLASSICS[3]!.id };

const sequenceClassic = (c: (typeof SEQUENCE_CLASSICS)[number]): Plan => {
  const schedule = buildSchedule(c.books, c.days);
  return {
    id: c.id,
    classic: true,
    title: c.title,
    description: c.description,
    author: null,
    days: schedule.length,
    books: c.books,
    companion: c.companionWork ? `${c.companionWork.title} · ${c.companionWork.author}` : null,
    companionWork: c.companionWork ?? null,
    restSundays: false,
    visibility: "public",
    scheduleKind: "sequence",
    packageId: null,
    prompts: DEFAULT_PROMPTS,
    createdAt: CREATED,
    schedule,
  };
};

/** A work read alongside: its section readings and the plan's companion, from the static library (public/library). */
async function alongsideOf(a: { libraryId: string; sectionUids?: string[] }): Promise<{ sections: SectionReading[]; work: CompanionWork }> {
  const [catalogue, toc] = await Promise.all([loadCatalogue(), loadToc(a.libraryId)]);
  const w = catalogue.works.find((x) => x.id === a.libraryId);
  if (!w) throw new ApiError("That book isn’t in the library.", "validation", 400);
  const sections = sectionReadings(w, toc, a.sectionUids);
  if (a.sectionUids && sections.length !== new Set(a.sectionUids).size) throw new ApiError("One of those sections isn’t in the book.", "validation", 400);
  return { sections, work: { libraryId: w.id, title: w.title, author: w.author } };
}

/** The Mortification's fourteen chapters (Owen, Works vol. 6): a paid-or-app-only reading, shown dimmed on the web. */
const MORTIFICATION = Array.from({ length: 14 }, (_, i) => `mortification-of-sin-in-believers/${i + 4}`);

const summary = (p: Plan): PlanSummary => {
  const { schedule: _s, ...rest } = p;
  return rest;
};

export function createMockPlans(me: UserCard, friends: UserCard[]): PlanSource {
  const ruth = friends.find((f) => f.handle === "ruth") ?? u("u-ruth", "ruth", "Ruth Adeyemi");
  const tom = friends.find((f) => f.handle === "tom") ?? u("u-tom", "tom", "Thomas Bell");
  const grace = friends.find((f) => f.handle === "grace") ?? u("u-grace", "grace", "Grace Mensah");
  const today = () => localDate();
  const ago = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();

  const friendsPlan = (
    id: string,
    author: UserCard,
    title: string,
    description: string,
    books: string[],
    days: number,
    restSundays: boolean,
    prompts = DEFAULT_PROMPTS,
    alongside?: { sections: SectionReading[]; work: CompanionWork },
  ): Plan => {
    const schedule = buildSchedule(books, days, alongside?.sections);
    return {
      id,
      classic: false,
      title,
      description,
      author,
      days: schedule.length,
      books,
      companion: alongside ? `${alongside.work.title} · ${alongside.work.author}` : null,
      companionWork: alongside?.work ?? null,
      restSundays,
      visibility: "friends",
      scheduleKind: "sequence",
      packageId: null,
      prompts,
      createdAt: CREATED,
      schedule,
    };
  };

  type State = { plans: Plan[]; journeys: Journey[]; pending: (Invitation & { code: string })[]; notes: DayNote[] };
  let ready: Promise<State> | null = null;

  const journeyOf = (plan: Plan, j: Partial<Journey> & Pick<Journey, "id" | "startedOn" | "together" | "follow" | "inviteCode" | "members">): Journey => ({
    plan,
    invited: [],
    invitePath: `/plans/join/${j.inviteCode}`,
    createdAt: CREATED,
    me: { paused: false, startOffset: 0, read: j.members.find((m) => m.isMe)?.read ?? [], joinedAt: CREATED, day: 1, first: 1, today: null },
    ...j,
  });
  /** As the API sends a journey: with the reader's place today (`me.day`, `first`, `today`). */
  const viewed = (j: Journey): Journey => ({ ...j, me: { ...j.me, ...placeToday(j, today()) } });

  async function load(): Promise<State> {
    const [m, b] = await Promise.all([import("./catalog/mcheyne-1842.json"), import("./catalog/bcp-1871-daily.json")]);
    const mcheyne = fromCatalog(m.default as CatalogFile);
    const lectionary = fromCatalog(b.default as CatalogFile);
    const [john, psalms, romans, hebrews] = SEQUENCE_CLASSICS.map(sequenceClassic) as [Plan, Plan, Plan, Plan];
    // Friends' plans with a book read alongside. The library is static files; without them (unit
    // tests outside a browser) these two plans are simply left out.
    const [catechism, mortification] = await Promise.all([
      alongsideOf({ libraryId: "westminster-shorter-catechism" }).catch(() => null),
      alongsideOf({ libraryId: "john-owen-works-vol-6", sectionUids: MORTIFICATION }).catch(() => null),
    ]);
    const withCatechism = catechism
      ? friendsPlan(
          "john-catechism",
          ruth,
          "John with the Shorter Catechism",
          "The Gospel of John, and the Westminster Shorter Catechism beside it: a few questions a day, with their proofs.",
          ["John"],
          36,
          false,
          DEFAULT_PROMPTS,
          catechism,
        )
      : null;
    const withOwen = mortification
      ? friendsPlan(
          "mortification-psalms",
          tom,
          "Mortification with the Psalms",
          "Owen’s Of the Mortification of Sin in Believers, a chapter every other day, with five psalms a day beside it.",
          ["Ps"],
          30,
          false,
          ["What is Owen asking of you?", ...DEFAULT_PROMPTS],
          mortification,
        )
      : null;

    const t = today();
    // M'Cheyne by date, begun a month ago and three days behind: the catch-up offer shows.
    const mStart = addDays(t, -30);
    const mFirst = appointedOrdinal(mcheyne.schedule.map((d) => d.key), mStart);
    const mToday = appointedOrdinal(mcheyne.schedule.map((d) => d.key), t);
    const mRead = Array.from({ length: (mToday - mFirst + 365) % 365 - 2 }, (_, i) => ((mFirst - 1 + i) % 365) + 1);
    const memberMe = (read: number[], joinedAt = CREATED) => ({ user: me, read, joinedAt, isMe: true });
    const member = (user: UserCard, read: number[]) => ({ user, read, joinedAt: CREATED, isMe: false });

    return {
      plans: [mcheyne, lectionary, john, psalms, romans, hebrews, ...(withCatechism ? [withCatechism] : []), ...(withOwen ? [withOwen] : [])],
      journeys: [
        journeyOf(john, {
          id: "j-john",
          startedOn: addDays(t, -4),
          together: true,
          follow: "fromDayOne",
          inviteCode: "j0hn21",
          members: [memberMe(range(1, 4)), member(ruth, range(1, 5)), member(tom, range(1, 3))],
        }),
        journeyOf(mcheyne, { id: "j-mcheyne", startedOn: mStart, together: false, follow: "byDate", inviteCode: "mch365", members: [memberMe(mRead)] }),
        ...(withCatechism
          ? [journeyOf(withCatechism, { id: "j-catechism", startedOn: addDays(t, -1), together: false, follow: "fromDayOne", inviteCode: "wsc36j", members: [memberMe([1])] })]
          : []),
      ],
      pending: [
        {
          journeyId: "j-psalms-grace",
          from: grace,
          plan: summary(psalms),
          startedOn: addDays(t, -1),
          members: [grace],
          invitedAt: ago(20),
          code: "ps30gm",
        },
      ],
      notes: [
        { id: "dn-1", journeyId: "j-john", day: 4, author: tom, body: "“Sir, give me this water.” She still thinks he means the well.", createdAt: ago(30), hidden: false, canDelete: false },
        { id: "dn-2", journeyId: "j-john", day: 4, author: ruth, body: "And he doesn’t correct her quickly. He lets the conversation go deeper.", createdAt: ago(28), hidden: false, canDelete: false },
        { id: "dn-3", journeyId: "j-john", day: 5, author: ruth, body: "“Do you want to be healed?” Thirty-eight years, and he asks. I needed that question this morning.", createdAt: ago(3), hidden: false, canDelete: false },
      ],
    };
  }

  const state = () => (ready ??= load());
  const notFound = () => new ApiError("This plan isn’t available.", "not-found", 404);

  async function find(id: string) {
    const s = await state();
    const j = s.journeys.find((x) => x.id === id);
    if (!j) throw notFound();
    return { s, j: structuredClone(j) };
  }

  async function save(s: State, j: Journey) {
    // The reader's own row in members mirrors `me`.
    j.members = j.members.map((m) => (m.isMe ? { ...m, read: j.me.read } : m));
    s.journeys = s.journeys.map((x) => (x.id === j.id ? j : x));
    return wait(viewed(j), 60);
  }

  async function respond(journeyId: string, accept: boolean): Promise<Journey | null> {
    const s = await state();
    const inv = s.pending.find((i) => i.journeyId === journeyId);
    if (!inv) throw new ApiError("This invitation has been withdrawn.", "not-found", 404);
    s.pending = s.pending.filter((i) => i.journeyId !== journeyId);
    if (!accept) return wait(null);
    const plan = s.plans.find((p) => p.id === inv.plan.id)!;
    const j = journeyOf(plan, {
      id: journeyId,
      startedOn: inv.startedOn,
      together: true,
      follow: plan.scheduleKind === "calendar" ? "byDate" : "fromDayOne",
      inviteCode: inv.code,
      members: [
        ...inv.members.map((m) => ({ user: m, read: [1], joinedAt: inv.invitedAt, isMe: false })),
        { user: me, read: [], joinedAt: new Date().toISOString(), isMe: true },
      ],
    });
    s.journeys = [j, ...s.journeys];
    return wait(viewed(j), 150);
  }

  const sorted = (xs: number[]) => [...new Set(xs)].sort((a, b) => a - b);

  return {
    plans: async () => wait((await state()).plans.map(summary)),
    plan: async (id) => wait((await state()).plans.find((p) => p.id === id) ?? null),
    async createPlan(input) {
      const s = await state();
      check(input.title);
      check(input.description);
      if (!input.books.length && !input.alongside) throw new ApiError("Choose at least one book, or a book to read alongside.", "validation", 400);
      const alongside = input.alongside ? await alongsideOf(input.alongside) : null;
      const schedule = buildSchedule(input.books, input.days, alongside?.sections);
      const plan: Plan = {
        id: `p-${crypto.randomUUID().slice(0, 8)}`,
        classic: false,
        title: input.title.trim(),
        description: input.description.trim() || null,
        author: me,
        days: schedule.length,
        books: input.books,
        companion: alongside ? `${alongside.work.title} · ${alongside.work.author}` : null,
        companionWork: alongside?.work ?? null,
        restSundays: input.restSundays,
        visibility: input.visibility,
        scheduleKind: "sequence",
        packageId: null,
        prompts: DEFAULT_PROMPTS,
        createdAt: new Date().toISOString(),
        schedule,
      };
      s.plans = [...s.plans, plan];
      return wait(plan, 200);
    },
    journeys: async () => wait((await state()).journeys.map(viewed)),
    async journey(id) {
      const j = (await state()).journeys.find((x) => x.id === id);
      return wait(j ? viewed(j) : null);
    },
    async start(planId, { together, invite, startOn, follow }) {
      const s = await state();
      const plan = s.plans.find((p) => p.id === planId);
      if (!plan) throw notFound();
      const invited = friends.filter((f) => invite.includes(f.id));
      const code = crypto.randomUUID().replace(/-/g, "").slice(0, 10);
      const j = journeyOf(plan, {
        id: `j-${crypto.randomUUID().slice(0, 8)}`,
        startedOn: startOn,
        together: together || invited.length > 0,
        follow: follow ?? (plan.scheduleKind === "calendar" ? "byDate" : "fromDayOne"),
        inviteCode: code,
        members: [{ user: me, read: [], joinedAt: new Date().toISOString(), isMe: true }],
        invited,
      });
      s.journeys = [j, ...s.journeys];
      return wait(viewed(j), 200);
    },
    async setRead(journeyId, day, read) {
      const { s, j } = await find(journeyId);
      j.me.read = read ? sorted([...j.me.read, day]) : j.me.read.filter((d) => d !== day);
      return save(s, j);
    },
    async setPaused(journeyId, paused) {
      const { s, j } = await find(journeyId);
      j.me.paused = paused;
      return save(s, j);
    },
    async catchUp(journeyId, how, date) {
      const { s, j } = await find(journeyId);
      // As the API's updateJourney (catchUp), from the same placeOn.
      const at = { follow: j.follow, keys: j.plan.schedule?.map((d) => d.key) ?? [], startedOn: j.startedOn, restSundays: j.plan.restSundays, days: j.plan.days };
      const { slot, first } = placeOn({ ...at, today: date, startOffset: j.me.startOffset });
      if (how === "mark-read") {
        // Every day before today: by date across the new year, the rest of last year's table and this year's start.
        const upTo = Math.min(slot - 1, j.plan.days);
        const ranges: [number, number][] =
          slot === first ? [] : upTo >= first ? [[first, upTo]] : j.follow === "byDate" ? [[first, j.plan.days], [1, upTo]] : [];
        j.me.read = sorted([...j.me.read, ...ranges.flatMap(([a, b]) => range(a, b))]);
      } else if (j.follow === "byDate") {
        // Today's day is the calendar's: move where this reader's year begins, wrapping past its end.
        const base = placeOn({ ...at, today: j.startedOn, startOffset: 0 }).day;
        j.me.startOffset = (slot - base + j.plan.days) % j.plan.days;
      } else {
        // Move day 1 so the next unread day is today.
        const raw = slotOn(j.startedOn, date, j.plan.restSundays);
        const next = Math.min((j.me.read.at(-1) ?? 0) + 1, j.plan.days);
        j.me.startOffset = Math.max(0, raw - next);
      }
      return save(s, j);
    },
    async invite(journeyId, userIds) {
      const { s, j } = await find(journeyId);
      const add = friends.filter((f) => userIds.includes(f.id) && !j.members.some((m) => m.user.id === f.id) && !j.invited.some((i) => i.id === f.id));
      j.together = true;
      j.invited = [...j.invited, ...add];
      await save(s, j);
    },
    async withdrawInvite(journeyId, userId) {
      const { s, j } = await find(journeyId);
      j.invited = j.invited.filter((x) => x.id !== userId);
      await save(s, j);
    },
    async join(code) {
      const s = await state();
      const mine = s.journeys.find((j) => j.inviteCode === code);
      if (mine) return wait(viewed(mine));
      const inv = s.pending.find((i) => i.code === code);
      if (!inv) throw new ApiError("This invite link isn’t valid any more. Ask for a new one.", "not-found", 404);
      return (await respond(inv.journeyId, true))!;
    },
    async leave(journeyId) {
      const s = await state();
      s.journeys = s.journeys.filter((j) => j.id !== journeyId);
      return wait(undefined);
    },
    invitations: async () => wait((await state()).pending.map(({ code: _c, ...i }) => i)),
    respond,
    async notes(journeyId, day) {
      const { s, j } = await find(journeyId);
      const prompts = j.plan.prompts.length ? j.plan.prompts : DEFAULT_PROMPTS;
      return wait({ prompt: prompts[(day - 1) % prompts.length]!, items: s.notes.filter((n) => n.journeyId === journeyId && n.day === day) });
    },
    async addNote(journeyId, day, body) {
      const { s, j } = await find(journeyId);
      if (!j.together) throw new ApiError("Day conversations are for plans read together. Invite someone first.", "conflict", 409);
      check(body);
      const n: DayNote = { id: `dn-${crypto.randomUUID()}`, journeyId, day, author: me, body: body.trim(), createdAt: new Date().toISOString(), hidden: false, canDelete: true };
      s.notes = [...s.notes, n];
      return wait(n, 150);
    },
    async deleteNote(_journeyId, noteId) {
      const s = await state();
      s.notes = s.notes.filter((n) => n.id !== noteId);
      return wait(undefined);
    },
  };
}
